import {
  AuthChangePasswordDto,
  AuthChangePasswordResponseDto,
  AuthPasswordResetDto,
  AuthPasswordResetResponseDto,
  AuthSignInDto,
  AuthSignUpDto,
  AuthSignUpResponseDto,
  AuthUpdateProfileDto,
  AuthUserDto,
} from "types/api/auth";
import { BooksTransactionsHistoryParamsDto } from "types/api/books";
import {
  AddInventoryParcelItemsDto,
  AddonsMethodsResponseDto,
  AddParcelsDto,
  CartResponseDto,
  ConsolidationAddonsMethodsResponseDto,
  CustomInformationDto,
  DeleteCartResponseDto,
  DeliveryShippingMethodsResponseDto,
  PackingMethodsResponseDto,
  SetAddonsMethodDto,
  SetAddressDto,
  SetConsolidationAddonsMethodsDto,
  SetDeliveryMethodDto,
  SetPackingMethodDto,
  SetPartnerIdDto,
  SetPartnerIdResponseDto,
} from "types/api/cart";
import { ImportCustomsDeclarationDto } from "types/api/common";
import { ContainerDetail, ContainerSummary } from "types/api/containers";
import { DestinationAddressDto } from "types/api/destination_addresses";
import {
  CreateCustomsDeclarationsForExpectedItemDto,
  CreateExpectedShipmentDto,
  CustomsDeclarationsForExpectedItemResponseDto,
  ExpectedShipmentsDto,
  UpdateExpectedShipmentDto,
} from "types/api/expected";
import { KnowledgeDto, KnowledgeSectionDto } from "types/api/help";
import {
  HistoryDetailedParamsDto,
  HistoryFullDetailedItemResponseDto,
  HistoryParamsDto,
  HistoryResponseDto,
  ManifestDetail,
  ManifestListParams,
} from "types/api/history";
import {
  HomeArticleDto,
  HomeArticleResponse,
  HomeSectionContentResponse,
  HomeSectionsResponse,
} from "types/api/home";
import {
  CreateInboundAddressDto,
  CreateInboundShipmentDto,
  InboundAddressDto,
  InboundEstimateResponseDto,
  InboundLabelCancelDto,
  InboundShipmentDetailDto,
  InboundShipmentDto,
  InboundShipmentsDto,
  InboundShipResponseDto,
  ShipInboundShipmentDto,
  UpdateInboundAddressDto,
  UpdateInboundShipmentDto,
} from "types/api/inbound";
import {
  InventoryCustomsDeclaration,
  ItemsPutAwayDto,
  ItemsReceivedItemDto,
  ReplaceInventoryCustomsDeclarationsDto,
} from "types/api/items";
import { MailboxArticlesResponse, MailboxResponseDto } from "types/api/mailbox";
import { OutgoingDto, OutgoingResponseDto } from "types/api/outgoing";
import {
  PurchaseAllConformationsResponseDto,
  PurchaseAllPayPalCaptureOrderDto,
  PurchaseConsolidationCreditCardCompleteDto,
  PurchaseConsolidationPaypalsCompleteDto,
} from "types/api/purchase_all";
import {
  OverpayCompleteDto,
  RechargeCreditCardDto,
  RechargeOverpayCheckoutDto,
  RechargePayPalCompleteDto,
  RechargePayPalCreateOrderDto,
} from "types/api/recharge";
import {
  CreateServiceRequestDto,
  ServiceRequestsResponseDto,
} from "types/api/service_requests";
import {
  CreateShipmentsCustomDeclarationDto,
  ShipmentsBulkUpdateCommentDto,
  ShipmentsUpdateCommentDto,
  ShopsResponseDto,
  StorageDto,
  StorageResponseDto,
  TransferringDto,
  UpdateAddonsDto,
} from "types/api/shipments";
import {
  CreateSmsSubscriptionDto,
  SmsMessagesParamsDto,
} from "types/api/smsNumbers";
import {
  StripePaymentIntentCompleteDto,
  StripePurchaseAllCompleteDto,
  StripePurchaseAllCreateDto,
  StripeRechargeCreateDto,
} from "types/api/stripe";
import {
  WarehouseBranchResponseDto,
  WarehouseInfoResponseDto,
} from "types/api/warehouse";
import {
  BooksCreateBalanceVoucherFormValues,
  BooksRedeemVoucherFormValues,
} from "../types/books";
import client from "./client";
import { currentPartner } from "./currentPartner";

// Partner's KB-path prefix. Cached at module load via currentPartner — the
// hostname read happens once in utils/currentPartner.ts, not on every KB call.
const homePrefix = currentPartner.api.homeCardsPrefix;

// Multipart uploads run as long as the file takes; everything else gets the
// client's default timeout (REQUEST_TIMEOUT_MS in client.js).
export const UPLOAD_REQUEST_OPTIONS = { timeout: 0 };

// The only network the deposit address supports for now.
export const USDT_NETWORK = "TRC20";

const API = {
  auth: {
    signIn: (data: AuthSignInDto): Promise<AuthSignUpResponseDto> =>
      client.post("/auth/sign_in", data),
    logOut: () => client.delete("/auth/sign_out"),
    signUp: (data: AuthSignUpDto): Promise<AuthSignUpResponseDto> =>
      client.post("/auth", data),
    getProfile: (): Promise<AuthUserDto> => client.get("/user"),
    requestPasswordReset: (
      data: AuthPasswordResetDto,
    ): Promise<AuthPasswordResetResponseDto> =>
      client.post("/auth/password", data),
    changePassword: (
      data: AuthChangePasswordDto,
    ): Promise<AuthChangePasswordResponseDto> =>
      client.patch("/auth/password", data),
    updateProfile: (
      data: AuthUpdateProfileDto,
    ): Promise<AuthSignUpResponseDto> => client.patch("/auth", data),
  },

  warehouse: {
    getWarehouseInfo: (): Promise<WarehouseInfoResponseDto> =>
      client.get("/dashboard"),
    getWarehouseBranches: (): Promise<WarehouseBranchResponseDto> =>
      client.get("/branches"),
  },
  home: {
    getHomeSections: ({
      currentLocale,
    }: KnowledgeDto): Promise<HomeSectionsResponse> =>
      client.get(`/kb/${homePrefix}${currentLocale.toLowerCase()}/`),
    getHomeSectionContent: ({
      currentLocale,
      code,
    }: KnowledgeSectionDto): Promise<HomeSectionContentResponse> =>
      client.get(`/kb/${homePrefix}${currentLocale.toLowerCase()}/${code}`),
    getHomeArticle: ({
      currentLocale,
      sectionCode,
      articleCode,
    }: HomeArticleDto): Promise<HomeArticleResponse> =>
      client.get(
        `/kb/${homePrefix}${currentLocale.toLowerCase()}/${sectionCode}/${articleCode}`,
      ),
  },
  help: {
    getKnowledgeBaseHelpers: ({
      currentLocale,
    }: KnowledgeDto): Promise<unknown> =>
      client.get(`/kb/${currentLocale.toLowerCase()}/helpers`),
    getKnowledgeBase: ({ currentLocale }: KnowledgeDto): Promise<any> =>
      client.get(`/kb/${currentLocale.toLowerCase()}`),
    getKnowledgeBaseSection: ({
      currentLocale,
      code,
    }: KnowledgeSectionDto): Promise<any> =>
      client.get(`/kb/${currentLocale.toLowerCase()}/${code}`),
  },
  mailbox: {
    getMailboxes: (): Promise<MailboxResponseDto> => client.get("/mailboxes"),
    getMailboxArticles: ({
      currentLocale,
    }: KnowledgeDto): Promise<MailboxArticlesResponse> =>
      client.get(`/kb/helpers/mailboxes_${currentLocale.toLowerCase()}`),
  },
  destinations: {
    createAddress: (data: any) => client.post("/destination_addresses", data),
    getAddresses: (params: DestinationAddressDto) =>
      client.get("/destination_addresses", { params }),
    updateAddress: (id: number, data: any) =>
      client.patch(`/destination_addresses/${id}`, data),
    deleteAddress: ({ id }: { id: number }) =>
      client.delete(`/destination_addresses/${id}`),
    importCsv: (data: FormData) =>
      client.post(
        "/destination_addresses/import_csv",
        data,
        UPLOAD_REQUEST_OPTIONS,
      ),
    getImportCsvExample: () =>
      client.get("/destination_addresses/import_csv_example"),
  },
  addressParseJobs: {
    // Always multipart: raw_input and/or image, both top-level (backend spec).
    // Responses arrive Immutable-wrapped like every client call; use
    // utils/addressParseJob.ts, which unwraps them into AddressParseJobDto.
    create: (body: FormData) =>
      client.post("/address_parse_jobs", body, UPLOAD_REQUEST_OPTIONS),
    get: (id: number, options?: { signal?: AbortSignal; timeout?: number }) =>
      client.get(`/address_parse_jobs/${id}`, options),
  },
  // Async agent jobs; poll with utils/agentJob.ts until completed | failed.
  inboundShipmentParseJobs: {
    create: (data: { raw_input: string; inbound_address_id: number }) =>
      client.post("/inbound_shipment_parse_jobs", data),
    get: (id: number, options?: { signal?: AbortSignal; timeout?: number }) =>
      client.get(`/inbound_shipment_parse_jobs/${id}`, options),
  },
  invoiceParseJobs: {
    create: (body: FormData) => client.post("/invoice_parse_jobs", body),
    get: (id: number, options?: { signal?: AbortSignal; timeout?: number }) =>
      client.get(`/invoice_parse_jobs/${id}`, options),
  },
  passports: {
    getPassports: (params?: { search?: string }) =>
      client.get("/passports", { params }),
    createPassport: (data: any) => client.post("/passports", data),
  },
  serviceRequests: {
    getRequestTypes: (): Promise<ServiceRequestsResponseDto[]> =>
      client.get("/service_request_types"),
    getShipmentRequestType: (shipment_id: number) =>
      client.get(`/shipments/${shipment_id}/service_requests`),
    createServiceRequest: ({ itemId, ...data }: CreateServiceRequestDto) =>
      client.post(`/shipments/${itemId}/service_requests`, data),
    cancelServiceRequest: ({
      itemId,
      requestId,
    }: {
      itemId: number;
      requestId: number;
    }) =>
      client.put(
        `/shipments/${itemId}/service_requests/${requestId}/cancel_service_request`,
      ),
    cancelRequestInventoryServiceRequest: ({ taskId }: { taskId: number }) =>
      client.post(`/tasks/${taskId}/cancel`),
    createRequestInventoryShipment: ({ id }: { id: number }) =>
      client.post(`/items/${id}/create_unpack_task`),
    createRequestTransferShipments: ({
      ids,
      partnerTransferId,
    }: {
      ids: number[];
      partnerTransferId: string;
    }) =>
      client.post(`/shipments/request_transfer`, {
        ids,
        ffid: partnerTransferId,
      }),
    completeRequestTransferShipments: ({ codes }: { codes: string[] }) =>
      client.post(`/shipments/complete_transfer`, {
        codes,
      }),
    createRequestTransferInventoryItem: ({
      sku,
      partnerTransferId,
    }: {
      sku: string[];
      partnerTransferId: string;
    }) =>
      client.post(`/items/request_transfer`, {
        sku,
        sbid: partnerTransferId,
      }),
    completeRequestTransferInventoryItems: ({ codes }: { codes: string[] }) =>
      client.post(`/items/complete_transfer`, {
        code: codes[0],
      }),
  },
  shipments: {
    getStorage: ({ page, filter }: StorageDto): Promise<StorageResponseDto[]> =>
      client.get("/shipments", { params: { page, ...filter } }),
    getTransferring: ({
      filter,
    }: TransferringDto): Promise<StorageResponseDto[]> =>
      client.get("/shipments/transferring", { params: { ...filter } }),
    getShipment: (id: number) => client.get(`/shipments/${id}`),
    updateShipment: (data: any) => client.patch("/shipments/received", data),
    updateComment: (id: number, data: ShipmentsUpdateCommentDto) =>
      client.patch(`/shipments/${id}`, data),
    createCustomsDeclaration: ({
      itemId,
      data,
    }: CreateShipmentsCustomDeclarationDto) =>
      client.post(`/shipments/${itemId}/customs_declarations`, {
        customs_declarations: data,
      }),
    getShops: (): Promise<ShopsResponseDto[]> => client.get("/shop_emblems"),
    getExpectedShops: (): Promise<ShopsResponseDto[]> =>
      client.get("/expected_shipments/shop_emblems"),
    updateAddons: (
      id: number,
      data: UpdateAddonsDto,
      inventoryId: number | null,
    ) => {
      if (inventoryId) {
        return client.patch(`/items/${inventoryId}/update_addons`, {
          addons: data.addon_ids,
        });
      }
      return client.patch(`/shipments/${id}/update_addons`, data);
    },
    importCustomsDeclaration: ({ id, data }: ImportCustomsDeclarationDto) =>
      client.post(
        `/shipments/${id}/customs_declarations/import_declaration`,
        data,
        UPLOAD_REQUEST_OPTIONS,
      ),
    bulkUpdateComment: (data: ShipmentsBulkUpdateCommentDto) =>
      client.patch(`/shipments/bulk_update`, data),
  },
  cart: {
    getCart: (): Promise<CartResponseDto> => client.get("/current_parcel"),
    deleteCart: (): Promise<DeleteCartResponseDto> =>
      client.delete("/current_parcel"),
    addParcels: (data: AddParcelsDto) =>
      client.post("current_parcel_shipments", data),
    addInventoryParcelItems: (data: AddInventoryParcelItemsDto) =>
      client.post("current_parcel_items", data),
    setAddress: (data: SetAddressDto) =>
      client.post("/current_parcel/destination_addresses/select", data),
    setDeliveryMethod: (data: SetDeliveryMethodDto) =>
      client.patch("/current_parcel/update_preferred_carrier", data),
    setPackingMethods: (data: SetPackingMethodDto) =>
      client.post("/current_parcel/optional_line_items", data),
    setAddonsMethods: (data: SetAddonsMethodDto) =>
      client.patch("/current_parcel_items/update_items_addons", data),
    setConsolidationAddonsMethods: (data: SetConsolidationAddonsMethodsDto) =>
      client.post("/current_parcel/addons", data),
    getAllDeliveryMethods: (): Promise<DeliveryShippingMethodsResponseDto[]> =>
      client.get("/shipping_methods"),
    getDeliveryMethods: (): Promise<DeliveryShippingMethodsResponseDto[]> =>
      client.get("/current_parcel/shipping_methods"),
    getPackingMethods: (): Promise<PackingMethodsResponseDto[]> =>
      client.get("/current_parcel/optional_line_items"),
    getAddonsMethods: (): Promise<AddonsMethodsResponseDto[]> =>
      client.get("/addons"),
    deleteAddonsMethods: () =>
      client.delete("/current_parcel_items/delete_all_items_addons"),
    getConsolidationAddonsMethods: (): Promise<
      ConsolidationAddonsMethodsResponseDto[]
    > => client.get("/consolidation_addons"),
    // getConsolidationAddonsMethods: () => client.get('/current_parcel/addons'), // to fetch what we saved
    importCustomInformation: () =>
      client.post("/current_parcel/import_customs_informations"),
    createCustomsDeclaration: (data: CustomInformationDto) =>
      client.post("/current_parcel/customs_informations", {
        customs_informations: data,
      }),
    importCustomsDeclaration: ({ id, data }: ImportCustomsDeclarationDto) =>
      client.post(
        `/consolidations/${id}/customs_informations/import_declaration`,
        data,
        UPLOAD_REQUEST_OPTIONS,
      ),
    consolidateCart: () => client.post("/current_parcel/consolidate"),
    setPartnerId: (data: SetPartnerIdDto): Promise<SetPartnerIdResponseDto> =>
      client.patch(`/current_parcel/set_post_most_id`, data),
    getDescriptions: () => client.get("/hs_codes"),
  },
  outgoing: {
    getOutgoing: ({
      type,
      params,
    }: OutgoingDto): Promise<OutgoingResponseDto> =>
      client.get(`/outgoing/consolidations/${type}`, { params }),
    getDetailedItem: (id: number): Promise<OutgoingResponseDto> =>
      client.get(`/consolidations/${id}`),
    getCustomsDeclarations: (id: number) =>
      client.get(`/consolidations/${id}/customs_informations`),
    createCustomsDeclaration: ({
      itemId,
      data,
    }: {
      itemId: number;
      data: CustomInformationDto;
    }) =>
      client.post(`/consolidations/${itemId}/customs_informations`, {
        customs_informations: data,
      }),
    updateDeliveryMethod: (id: number, data: SetDeliveryMethodDto) =>
      client.patch(`/consolidations/${id}/update_preferred_carrier`, data),
    updateAddress: (id: number, data: any) =>
      client.patch(`/consolidations/${id}/update_address`, data),
    cancelConsolidation: (id: number) =>
      client.post(`/consolidations/${id}/cancel`),
    updateRequireInsurance: (id: number, data: any) =>
      client.patch(`/consolidations/${id}/update_require_insurance`, data),
    getDeliveryMethods: (id: number) =>
      client.get(`/consolidations/${id}/shipping_methods`),
    updateConsolidationAddons: (id: number, data: SetPackingMethodDto) =>
      client.post(`/consolidations/${id}/addons`, data),
    updateConsolidationOptionalLineItems: (
      id: number,
      data: SetPackingMethodDto,
    ) => client.post(`/consolidations/${id}/optional_line_items`, data),
    removeItemFromConsolidation: (consolidationId: number, id: number) =>
      client.delete(`/consolidations/${consolidationId}/items/${id}`),
    getConsolidationShipmentsCsv: (consolidationId: number) =>
      client.get(`/consolidations/${consolidationId}/shipments_export_as_csv`),
  },
  purchase: {
    prepaid: (id: number) => client.post(`/consolidations/${id}/prepaid`),
    createPayPalOrder: (id: number) =>
      client.post(`/purchase/consolidations/${id}/paypals/create_order`),
    checkoutOverpay: (id: number, data: any) =>
      client.post(`/purchase/consolidations/${id}/overpay/checkout`, data),
    confirmation: (id: number | string, data?: any) =>
      client.post(`/purchase/consolidations/${id}/confirmations`, data),
    capturePayPalOrder: (
      id: number,
      data: PurchaseConsolidationPaypalsCompleteDto,
    ) =>
      client.post(`/purchase/consolidations/${id}/paypals/capture_order`, data),
    completeOverpay: (id: number, data: OverpayCompleteDto) =>
      client.post(`/purchase/consolidations/${id}/overpay/complete`, data),
    createStripePaymentIntent: (id: number | string) =>
      client.post(
        `/purchase/consolidations/${id}/stripe/create_payment_intent`,
      ),
    completeStripePaymentIntent: (
      id: number | string,
      data: StripePaymentIntentCompleteDto,
    ) =>
      client.post(
        `/purchase/consolidations/${id}/stripe/complete_payment_intent`,
        data,
      ),
    checkoutCreditCard: (id: number) =>
      client.get(`purchase/consolidations/${id}/credit_cards/checkout`),
    completeCreditCard: (
      id: number,
      data: PurchaseConsolidationCreditCardCompleteDto,
    ) =>
      client.post(`purchase/consolidations/${id}/credit_cards/complete`, data),
  },
  purchase_all: {
    confirmation: (): Promise<PurchaseAllConformationsResponseDto> =>
      client.post(`purchase_all/confirmations`),
    create_paypal_order: (data: { sign_id: string }) =>
      client.post(`/purchase_all/paypals/create_order`, data),
    checkout_overpay: (data: any) =>
      client.post(`/purchase_all/overpay/checkout`, data),
    capture_paypal_order: (data: PurchaseAllPayPalCaptureOrderDto) =>
      client.post(`/purchase_all/paypals/capture_order`, data),
    complete_overpay: (data: OverpayCompleteDto) =>
      client.post(`/purchase_all/overpay/complete`, data),
    create_stripe_payment_intent: (data: StripePurchaseAllCreateDto) =>
      client.post(`/purchase_all/stripe/create_payment_intent`, data),
    complete_stripe_payment_intent: (data: StripePurchaseAllCompleteDto) =>
      client.post(`/purchase_all/stripe/complete_payment_intent`, data),
  },
  stripe_transactions: {
    get: (paymentIntentId: string) =>
      client.get(`/stripe_transactions/${encodeURIComponent(paymentIntentId)}`),
  },
  history: {
    getHistory: ({ year }: HistoryParamsDto): Promise<HistoryResponseDto> =>
      client.get("/history/orders", { params: { year } }),
    getHistoryDetailed: ({
      week_number,
      shipping_method,
      year,
      page,
    }: HistoryDetailedParamsDto) =>
      client.get(`/history/orders/week`, {
        params: { week_number, shipping_method, year, page },
      }),
    getHistoryFullDetailedItem: (
      id: number,
    ): Promise<HistoryFullDetailedItemResponseDto> =>
      client.get(`/consolidations/${id}`),
  },
  manifests: {
    getManifests: (params: ManifestListParams) =>
      client.get("/manifests", { params }),
    getManifest: (id: number): Promise<{ manifest: ManifestDetail }> =>
      client.get(`/manifests/${id}`),
  },
  containers: {
    getContainers: (): Promise<ContainerSummary[]> => client.get("/containers"),
    getContainer: (id: number): Promise<ContainerDetail> =>
      client.get(`/containers/${id}`),
  },
  books: {
    getBooksTransactionsHistory: ({
      page,
      filter,
    }: BooksTransactionsHistoryParamsDto) =>
      client.get("/account_transactions", { params: { page, ...filter } }),
    getBooksRecipients: ({ searchInAddress }: { searchInAddress: string }) =>
      client.get("/destination_addresses", { params: { searchInAddress } }),
  },
  items: {
    putAway: ({ id, data }: ItemsPutAwayDto) =>
      client.patch(`/items/${id}/put_away`, data),
    updateItemComment: (id: number, data: ItemsReceivedItemDto) =>
      client.patch(`/items/${id}/received_item`, data),
    getInventoryItems: ({ page, filter }: any) =>
      client.get("/items", { params: { page, ...filter } }),
    replaceCustomsDeclarations: ({
      itemId,
      data,
    }: ReplaceInventoryCustomsDeclarationsDto): Promise<
      InventoryCustomsDeclaration[]
    > =>
      client.post(`/items/${itemId}/customs_declarations`, {
        customs_declarations: data.map(
          ({
            description,
            quantity,
            value,
            weight,
            country_of_origin,
            hs_code,
            product_link,
          }) => ({
            description,
            quantity,
            value,
            weight,
            country_of_origin,
            hs_code,
            product_link,
          }),
        ),
      }),
  },
  expected: {
    getExpectedItems: ({ page, filter }: ExpectedShipmentsDto) =>
      client.get("/expected_shipments", { params: { page, ...filter } }),
    createExpectedItem: ({ expected_shipment }: CreateExpectedShipmentDto) =>
      client.post(`/expected_shipments`, { expected_shipment }),
    updateExpectedItem: ({
      expected_shipment,
      id,
    }: UpdateExpectedShipmentDto) =>
      client.patch(`/expected_shipments/${id}`, { expected_shipment }),
    deleteExpectedItem: (id: number) =>
      client.delete(`/expected_shipments/${id}`),
    getCustomsDeclarationsForExpectedItem: (
      id: number,
    ): Promise<CustomsDeclarationsForExpectedItemResponseDto[]> =>
      client.get(`/expected_shipments/${id}/customs_declarations`),
    createCustomsDeclarationForExpectedItem: ({
      itemId,
      data,
    }: CreateCustomsDeclarationsForExpectedItemDto) =>
      client.post(`/expected_shipments/${itemId}/customs_declarations`, {
        customs_declarations: data,
      }),
  },
  inbound: {
    getInboundAddresses: (): Promise<InboundAddressDto[]> =>
      client.get("/inbound_addresses"),
    createInboundAddress: ({ inbound_address }: CreateInboundAddressDto) =>
      client.post("/inbound_addresses", { inbound_address }),
    updateInboundAddress: ({ id, inbound_address }: UpdateInboundAddressDto) =>
      client.patch(`/inbound_addresses/${id}`, { inbound_address }),
    getInboundShipments: ({ page, filter }: InboundShipmentsDto) =>
      client.get("/inbound_shipments", { params: { page, ...filter } }),
    getInboundShipment: (id: number): Promise<InboundShipmentDetailDto> =>
      client.get(`/inbound_shipments/${id}`),
    createInboundShipment: ({ inbound_shipment }: CreateInboundShipmentDto) =>
      client.post("/inbound_shipments", { inbound_shipment }),
    updateInboundShipment: ({
      inbound_shipment,
      id,
    }: UpdateInboundShipmentDto) =>
      client.patch(`/inbound_shipments/${id}`, { inbound_shipment }),
    deleteInboundShipment: (id: number) =>
      client.delete(`/inbound_shipments/${id}`),
    estimateInboundShipment: (
      id: number,
    ): Promise<InboundEstimateResponseDto> =>
      client.post(`/inbound_shipments/${id}/shipengine/estimate`),
    shipInboundShipment: ({
      id,
      rate_id,
    }: ShipInboundShipmentDto): Promise<InboundShipResponseDto> =>
      client.post(`/inbound_shipments/${id}/shipengine/ship`, { rate_id }),
    cancelInboundLabel: (id: number): Promise<InboundLabelCancelDto> =>
      client.delete(`/inbound_shipments/${id}/shipengine/label_cancel`),
    // One CSV file = one shipment; each row = one package. `data` must carry
    // `file` and the required `inbound_address_id`.
    importInboundShipmentsCsv: (data: FormData): Promise<InboundShipmentDto> =>
      client.post(
        "/inbound_shipments/import_csv",
        data,
        UPLOAD_REQUEST_OPTIONS,
      ),
    getInboundCsvExample: () =>
      client.get("/inbound_shipments/import_csv_example"),
  },
  shopify: {
    getProfiles: () => client.get("/shopify/user_shopify_profiles"),
    createProfile: (data: any) =>
      client.post("/shopify/user_shopify_profiles", data),
    getProfile: (id: number) =>
      client.get(`/shopify/user_shopify_profiles/${id}`),
    updateProfile: (id: number, data: any) =>
      client.patch(`/shopify/user_shopify_profiles/${id}`, data),
    deleteProfile: (id: number) =>
      client.delete(`/shopify/user_shopify_profiles/${id}`),
    getItems: (profileId: number) =>
      client.get(`/shopify/user_shopify_profiles/${profileId}/shopify_items`),
    createItem: (profileId: number, data: any) =>
      client.post(
        `/shopify/user_shopify_profiles/${profileId}/shopify_items`,
        data,
      ),
    getItem: (profileId: number, itemId: number) =>
      client.get(
        `/shopify/user_shopify_profiles/${profileId}/shopify_items/${itemId}`,
      ),
    exportInventoryItemToShopify: (itemId: number, profileId: number) =>
      client.post(`/shopify/user_shopify_profiles/${profileId}/shopify_items`, {
        item_id: itemId,
      }),
  },
  externalPrintUsers: {
    getPrintUsers: () => client.get("/external_print_users"),
    createPrintUser: (data: any) => client.post("/external_print_users", data),
    updatePrintUser: (id: number, data: any) =>
      client.patch(`/external_print_users/${id}`, data),
    deletePrintUser: (id: number) =>
      client.delete(`/external_print_users/${id}`),
    updatePrintUserPassword: (id: number, data: any) =>
      client.patch(`/external_print_users/${id}/password`, data),
  },
  recharge: {
    rechargeBalance: ({ data }: RechargeCreditCardDto) =>
      client.post(`/recharge/credit_cards/complete`, data),
    rechargePayPalCreateOrder: (data: RechargePayPalCreateOrderDto) =>
      client.post(`/recharge/paypal/create_order`, data),
    rechargePayPalComplete: (data: RechargePayPalCompleteDto) =>
      client.post(`/recharge/paypal/capture_order`, data),
    rechargeOverpayCheckout: (data: RechargeOverpayCheckoutDto) =>
      client.post(`/recharge/overpay/checkout`, data),
    rechargeOverpayComplete: (data: OverpayCompleteDto) =>
      client.post(`/recharge/overpay/complete`, data),
    rechargeStripeCreatePaymentIntent: (data: StripeRechargeCreateDto) =>
      client.post(`/recharge/stripe/create_payment_intent`, data),
    rechargeStripeCompletePaymentIntent: (
      data: StripePaymentIntentCompleteDto,
    ) => client.post(`/recharge/stripe/complete_payment_intent`, data),
    getBooksVoucherHistory: () => client.get(`/client_vouchers`),
    createBooksVoucher: (data: BooksCreateBalanceVoucherFormValues) =>
      client.post(`/client_vouchers`, {
        client_voucher: {
          value: data.value,
        },
      }),
    redeemBooksVoucher: (data: BooksRedeemVoucherFormValues) =>
      client.post(`/client_vouchers/redeem`, data),
  },
  deposits: {
    // USDT (TRC-20) balance deposits to the user's permanent address.
    // Responses arrive Immutable-wrapped like every client call;
    // utils/usdtDeposits.ts unwraps them into the DTOs.
    wallets: () => client.get("/deposits/wallets"),
    // Creates the address on the first call and makes the backend scan it
    // more often for an hour — call it every time the deposit screen opens.
    address: (network = USDT_NETWORK) =>
      client.get("/deposits/address", { params: { network } }),
    // Scans the address now and every minute until `check_until`; 429 with
    // `retry_at` when asked again within a minute.
    check: (network = USDT_NETWORK) =>
      client.post("/deposits/check", null, { params: { network } }),
    list: (options?: { signal?: AbortSignal; timeout?: number }) =>
      client.get("/deposits", options),
  },
  telegramLink: {
    // The Telegram bot link (Account settings): one-time deep links into the
    // bot. Responses arrive Immutable-wrapped like every client call;
    // useTelegramLink unwraps them.
    create: () => client.post("/telegram_link"),
  },
  telegramChats: {
    // The chats the bot confirmed: the private chat and a group, at most one
    // of each. useTelegramLink polls `list` while a deep link is outstanding.
    list: () => client.get("/telegram_chats"),
    destroy: (id: number) => client.delete(`/telegram_chats/${id}`),
  },
  smsNumbers: {
    // Receive-only US SMS numbers (Utilities → SMS): a monthly subscription
    // paid from the balance plus the inbox. Immutable-wrapped like every
    // client call; useSmsSubscription/useSmsMessages unwrap them.
    options: () => client.get("/sms_subscription/options"),
    show: () => client.get("/sms_subscription"),
    create: (data: CreateSmsSubscriptionDto) =>
      client.post("/sms_subscription", data),
    cancel: () => client.post("/sms_subscription/cancel"),
    resume: () => client.post("/sms_subscription/resume"),
    messages: (params: SmsMessagesParamsDto) =>
      client.get("/sms_messages", { params }),
  },
};

export default API;
